/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, AppBar, Toolbar, Typography, Button, IconButton,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Paper, Chip, Avatar, Menu, MenuItem, ListItemIcon,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, TablePagination, InputAdornment, Tooltip, Divider,
    Switch, FormControlLabel, Alert, CircularProgress, Snackbar,
} from '@mui/material';
import {
    Search, Add, MoreVert, Edit, Delete,
    Visibility, VisibilityOff, People, PersonAdd, Email, Lock,
    ContentCopy, CheckCircleOutline,
} from '@mui/icons-material';
import { navLogoUrl } from './brandAssets';
import { useAuth } from './AuthContext';

const BASE_URL = process.env.REACT_APP_CONNECTOR_API_URL;

const EMPTY_FORM = { name: '', email: '', password: '', confirmPassword: '', isActive: true };

const AVATAR_COLORS = ['#1565c0', '#2e7d32', '#c62828', '#6a1b9a', '#e65100', '#00695c'];

function getInitials(name = '') {
    return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
}
function getAvatarColor(name = '') {
    return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
}
function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function AdminDashboard() {
    const navigate = useNavigate();
    const { token, logout } = useAuth();

    const [customers, setCustomers]       = useState([]);
    const [loading, setLoading]           = useState(true);
    const [apiError, setApiError]         = useState('');

    const [search, setSearch]             = useState('');
    const [page, setPage]                 = useState(0);
    const [rowsPerPage, setRowsPerPage]   = useState(5);

    const [actionMenu, setActionMenu]     = useState({ anchor: null, customer: null });

    // dialog state — shared for create & edit
    const [dialogOpen, setDialogOpen]     = useState(false);
    const [isEditMode, setIsEditMode]     = useState(false);
    const [editId, setEditId]             = useState(null);
    const [form, setForm]                 = useState(EMPTY_FORM);
    const [errors, setErrors]             = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm]   = useState(false);
    const [submitting, setSubmitting]     = useState(false);
    const [dialogError, setDialogError]   = useState('');
    const [createdCreds, setCreatedCreds] = useState(null); // { email, password }
    const [copySnack, setCopySnack]       = useState('');

    // ── API helpers ──────────────────────────────────────────────────────────

    const authHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
    }), [token]);

    // Wrapper around fetch that auto-logs-out on 401 (expired / invalid token)
    const authFetch = useCallback(async (url, options = {}) => {
        const res = await fetch(url, { ...options, headers: { ...authHeaders(), ...(options.headers || {}) } });
        if (res.status === 401) {
            logout();
            navigate('/login');
            return res; // caller won't proceed — navigate unmounts the component
        }
        return res;
    }, [authHeaders, logout, navigate]);

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        setApiError('');
        try {
            const res = await authFetch(`${BASE_URL}/api/customers`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            // Normalise: API returns companyName, we use name internally
            setCustomers(data.map(c => ({
                id:        c.id,
                name:      c.companyName,
                email:     c.email,
                isActive:  c.isActive,
                createdAt: c.createdAt,
            })));
        } catch (err) {
            setApiError('Failed to load customers. Please refresh.');
        } finally {
            setLoading(false);
        }
    }, [authFetch]);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

    // ── filtering / pagination ───────────────────────────────────────────────

    const filtered  = customers.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.email.toLowerCase().includes(search.toLowerCase())
    );
    const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // ── validation ───────────────────────────────────────────────────────────

    const validate = () => {
        const e = {};
        if (!form.name.trim())  e.name  = 'Company name is required.';
        if (!form.email.trim()) {
            e.email = 'Email is required.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            e.email = 'Invalid email format.';
        }
        if (!isEditMode) {
            if (!form.password.trim())         e.password = 'Password is required.';
            else if (form.password.length < 8) e.password = 'Must be at least 8 characters.';
            if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.';
        } else if (form.password) {
            if (form.password.length < 8) e.password = 'Must be at least 8 characters.';
            if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.';
        }
        return e;
    };

    // ── open / close dialog ──────────────────────────────────────────────────

    const openCreate = () => {
        setIsEditMode(false);
        setEditId(null);
        setForm(EMPTY_FORM);
        setErrors({});
        setDialogError('');
        setShowPassword(false);
        setShowConfirm(false);
        setDialogOpen(true);
    };

    const openEdit = (customer) => {
        setIsEditMode(true);
        setEditId(customer.id);
        setForm({ name: customer.name, email: customer.email, password: '', confirmPassword: '', isActive: customer.isActive });
        setErrors({});
        setDialogError('');
        setShowPassword(false);
        setShowConfirm(false);
        setDialogOpen(true);
        closeActionMenu();
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setForm(EMPTY_FORM);
        setErrors({});
        setDialogError('');
        setCreatedCreds(null);
    };

    const handleCopy = (text, label) => {
        navigator.clipboard.writeText(text).then(() => setCopySnack(`${label} copied!`));
    };

    // ── submit (create / edit) ───────────────────────────────────────────────

    const handleSubmit = async () => {
        const e = validate();
        if (Object.keys(e).length > 0) { setErrors(e); return; }

        setSubmitting(true);
        setDialogError('');

        try {
            if (isEditMode) {
                const passwordChanged = Boolean(form.password);
                const body = { companyName: form.name, email: form.email, isActive: form.isActive };
                if (passwordChanged) body.password = form.password;

                const res = await authFetch(`${BASE_URL}/api/customers/${editId}`, {
                    method: 'PUT',
                    body: JSON.stringify(body),
                });
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    setDialogError(data.error || 'Failed to update customer.');
                    return;
                }
                await fetchCustomers();
                if (passwordChanged) {
                    setCreatedCreds({ email: form.email.trim().toLowerCase(), password: form.password, isUpdate: true });
                } else {
                    closeDialog();
                }
                return;
            } else {
                const res = await authFetch(`${BASE_URL}/api/customers`, {
                    method: 'POST',
                    body: JSON.stringify({
                        companyName: form.name,
                        email:       form.email,
                        password:    form.password,
                        isActive:    form.isActive,
                    }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    setDialogError(data.error || 'Failed to create customer.');
                    return;
                }
                // Use form values — we already know them at submission time
                setCreatedCreds({ email: form.email.trim().toLowerCase(), password: form.password });
                await fetchCustomers();
                return;
            }
            closeDialog();
            await fetchCustomers();
        } catch {
            setDialogError('Unable to reach the server. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
        if (errors[name]) setErrors(err => ({ ...err, [name]: '' }));
    };

    // ── active toggle ────────────────────────────────────────────────────────

    const handleActiveToggle = async (customer) => {
        const newActive = !customer.isActive;
        // Optimistic update
        setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, isActive: newActive } : c));
        try {
            const res = await authFetch(`${BASE_URL}/api/customers/${customer.id}/active`, {
                method: 'PATCH',
                body: JSON.stringify({ isActive: newActive }),
            });
            if (!res.ok) {
                // Roll back on failure
                setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, isActive: !newActive } : c));
                setApiError('Failed to update status. Please try again.');
            }
        } catch {
            setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, isActive: !newActive } : c));
            setApiError('Failed to update status. Please try again.');
        }
    };

    // ── delete ───────────────────────────────────────────────────────────────

    const handleDelete = async (customer) => {
        closeActionMenu();
        if (!window.confirm(`Delete ${customer.name}? This cannot be undone.`)) return;
        try {
            const res = await authFetch(`${BASE_URL}/api/customers/${customer.id}`, {
                method: 'DELETE',
            });
            if (!res.ok) { setApiError('Failed to delete customer.'); return; }
            await fetchCustomers();
        } catch {
            setApiError('Unable to reach the server.');
        }
    };

    const closeActionMenu = () => setActionMenu({ anchor: null, customer: null });

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const contactUsUrl = process.env.REACT_APP_CONTACT_US_URL;

    // ── render ───────────────────────────────────────────────────────────────

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f5f7fa' }}>

            {/* ── AppBar — same style as existing NavBar ── */}
            <AppBar position="fixed" color="primary" elevation={1}>
                <Toolbar sx={{ minHeight: '64px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={navLogoUrl} alt="Zamp" style={{ height: 22, marginRight: 8, marginTop: 5 }} />
                        <Typography
                            variant="body2"
                            sx={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600, letterSpacing: 0.5 }}
                        >
                            Admin Console
                        </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button color="inherit" onClick={() => window.open(contactUsUrl, '_blank')}>
                        Contact Us
                    </Button>
                    <Button color="inherit" onClick={handleLogout}>
                        Logout
                    </Button>
                </Toolbar>
            </AppBar>

            {/* ── Main Content ── */}
            <Box sx={{ mt: 8, p: 3, flexGrow: 1 }}>

                {apiError && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setApiError('')}>
                        {apiError}
                    </Alert>
                )}

                {/* Page Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                        <Typography variant="h5" fontWeight={700} color="text.primary">Customers</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {customers.length} total · {customers.filter(c => c.isActive).length} active
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={openCreate}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 2.5 }}
                    >
                        Create Customer
                    </Button>
                </Box>

                {/* Search */}
                <Paper elevation={0} sx={{ mb: 2, borderRadius: 2, border: '1px solid #e0e7ef', backgroundColor: '#fff' }}>
                    <TextField
                        fullWidth
                        placeholder="Search by name or email…"
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 2 },
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        }}
                    />
                </Paper>

                {/* Table */}
                <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e7ef', overflow: 'hidden' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                                        {['Customer', 'Active', 'Created', ''].map((h, i) => (
                                            <TableCell
                                                key={i}
                                                align={h === '' ? 'right' : 'left'}
                                                sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5 }}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginated.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 8, color: 'text.secondary' }}>
                                                <People sx={{ fontSize: 40, mb: 1, opacity: 0.3, display: 'block', mx: 'auto' }} />
                                                <Typography variant="body2">No customers found</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : paginated.map(customer => (
                                        <TableRow key={customer.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>

                                            {/* Customer */}
                                            <TableCell sx={{ py: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ bgcolor: getAvatarColor(customer.name), width: 36, height: 36, fontSize: '0.78rem', fontWeight: 700 }}>
                                                        {getInitials(customer.name)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600}>{customer.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{customer.email}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            {/* Active status */}
                                            <TableCell sx={{ py: 1.5 }}>
                                                <Chip
                                                    label={customer.isActive ? 'Active' : 'Inactive'}
                                                    color={customer.isActive ? 'success' : 'error'}
                                                    size="small"
                                                    sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                                />
                                            </TableCell>

                                            {/* Created */}
                                            <TableCell sx={{ py: 1.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {formatDate(customer.createdAt)}
                                                </Typography>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="right" sx={{ py: 1.5 }}>
                                                <Tooltip title="More actions">
                                                    <IconButton size="small" onClick={(e) => setActionMenu({ anchor: e.currentTarget, customer })}>
                                                        <MoreVert fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filtered.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />
                </Paper>
            </Box>

            {/* ── Row Actions Menu ── */}
            <Menu
                anchorEl={actionMenu.anchor}
                open={Boolean(actionMenu.anchor)}
                onClose={closeActionMenu}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 150 } }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => openEdit(actionMenu.customer)}>
                    <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
                    Edit
                </MenuItem>
                <Divider />
                <MenuItem sx={{ color: 'error.main' }} onClick={() => handleDelete(actionMenu.customer)}>
                    <ListItemIcon><Delete fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                    Delete
                </MenuItem>
            </Menu>

            {/* ── Create / Edit Dialog ── */}
            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pb: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 2, backgroundColor: 'primary.light', opacity: 0.8 }}>
                            <PersonAdd sx={{ color: 'primary.dark', fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {createdCreds?.isUpdate ? 'Edit Customer' : isEditMode ? 'Edit Customer' : 'Create New Customer'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {createdCreds?.isUpdate ? 'Password updated' : isEditMode ? 'Update customer details below' : 'Fill in the details below'}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {createdCreds ? (
                        /* ── Credentials display after successful creation / password update ── */
                        <Box sx={{ pt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <CheckCircleOutline sx={{ color: 'success.main', fontSize: 28 }} />
                                <Typography variant="body1" fontWeight={600} color="success.main">
                                    {createdCreds.isUpdate ? 'Password updated successfully!' : 'Customer created successfully!'}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {createdCreds.isUpdate
                                    ? ''
                                    : 'Share these credentials with the customer. This is the only time the password will be shown.'}
                            </Typography>

                            {/* Email row */}
                            <Paper variant="outlined" sx={{ borderRadius: 2, mb: 1.5, px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{createdCreds.email}</Typography>
                                </Box>
                                <Tooltip title="Copy email">
                                    <IconButton size="small" onClick={() => handleCopy(createdCreds.email, 'Email')}>
                                        <ContentCopy fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Paper>

                            {/* Password row */}
                            <Paper variant="outlined" sx={{ borderRadius: 2, px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">Password</Typography>
                                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>{createdCreds.password}</Typography>
                                </Box>
                                <Tooltip title="Copy password">
                                    <IconButton size="small" onClick={() => handleCopy(createdCreds.password, 'Password')}>
                                        <ContentCopy fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Paper>
                        </Box>
                    ) : (
                        <>
                            {dialogError && (
                                <Alert severity="error" sx={{ mt: 1, mb: 1, borderRadius: 2 }}>
                                    {dialogError}
                                </Alert>
                            )}

                            {/* Company Name */}
                            <TextField
                                fullWidth label="Company Name" name="name"
                                value={form.name} onChange={handleFormChange}
                                error={Boolean(errors.name)} helperText={errors.name}
                                margin="normal" autoFocus
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><People sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>,
                                }}
                            />

                            {/* Email */}
                            <TextField
                                fullWidth label="Email Address" name="email" type="email"
                                value={form.email} onChange={handleFormChange}
                                error={Boolean(errors.email)} helperText={errors.email}
                                margin="normal"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Email sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>,
                                }}
                            />

                            {/* Password */}
                            <TextField
                                fullWidth
                                label={isEditMode ? 'New Password (leave blank to keep current)' : 'Password'}
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                value={form.password} onChange={handleFormChange}
                                error={Boolean(errors.password)} helperText={errors.password}
                                margin="normal"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Lock sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowPassword(p => !p)} edge="end">
                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Confirm Password */}
                            <TextField
                                fullWidth
                                label="Confirm Password"
                                name="confirmPassword"
                                type={showConfirm ? 'text' : 'password'}
                                value={form.confirmPassword} onChange={handleFormChange}
                                error={Boolean(errors.confirmPassword)} helperText={errors.confirmPassword}
                                margin="normal"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Lock sx={{ color: 'text.secondary', fontSize: 20 }} /></InputAdornment>,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowConfirm(p => !p)} edge="end">
                                                {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {/* Active toggle */}
                            <Box sx={{ mt: 2, px: 0.5 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="isActive"
                                            checked={form.isActive}
                                            onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))}
                                            color="success"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" fontWeight={500}>
                                            {form.isActive ? 'Active' : 'Inactive'}
                                        </Typography>
                                    }
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    {createdCreds ? (
                        <Button onClick={closeDialog} variant="contained" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                            Done
                        </Button>
                    ) : (
                        <>
                            <Button onClick={closeDialog} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={submitting}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                            >
                                {submitting
                                    ? (isEditMode ? 'Updating…' : 'Creating…')
                                    : (isEditMode ? 'Update Customer' : 'Create Customer')}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
            <Snackbar
                open={Boolean(copySnack)}
                autoHideDuration={2000}
                onClose={() => setCopySnack('')}
                message={copySnack}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}

export default AdminDashboard;
